import { useState } from 'react';
import RegisterLink from '@/components/ui/RegisterLink';

const METHODS = [
  {
    id: 'payid', icon: 'ri-bank-line', name: 'PayID', badge: 'Most Popular', badgeColor: 'text-emerald-400',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: '< 10 minutes', fees: 'Free', avail: '24/7',
    desc: "PayID is Australia's real-time bank transfer system linked directly to your mobile number or email address. It is the fastest and most widely used deposit method at Bybid9. No card numbers, no BSB, just your PayID and the funds arrive instantly. Always check the available bank account before depositing as our bank account for deposit are always changing.",
    pros: ['Instant deposits, no waiting', 'No transaction fees', 'Linked to your existing bank account', 'Available 24/7 including weekends and public holidays', 'Supported by all major Australian banks (ANZ, CBA, NAB, Westpac, etc.)', 'No third party apps required'],
    cons: ['Requires an Australian bank account', 'Daily transfer limits may apply depending on your bank', 'Bank account for deposit always changing. Always check before depositing'],
    how: 'Go to Deposit. Select PayID. Copy our PayID address. Open your banking app. Send to PayID. Funds credited instantly.'
  },
  {
    id: 'osko', icon: 'ri-flashlight-line', name: 'OSKO', badge: 'Ultra Fast', badgeColor: 'text-yellow-400',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: 'instant', fees: 'Free', avail: '24/7',
    desc: 'OSKO is a fast payment service built on the New Payments Platform (NPP). It enables near-instant bank transfers 24/7, making it one of the quickest ways to fund your Bybid9 account.',
    pros: ['Instant transfers', 'Available 24/7', 'No fees', 'Supported by major Australian banks'],
    cons: ['Requires NPP enabled bank account', 'Always check available bank account before depositing'],
    how: 'Go to Deposit. Select OSKO. Enter our BSB and account number. Confirm transfer in your banking app.'
  },
  {
    id: 'gpay', icon: 'ri-google-line', name: 'Google Pay', badge: 'Mobile Friendly', badgeColor: 'text-[#47CAF1]',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: '< 15 minutes', fees: 'Free', avail: '24/7',
    desc: 'Google Pay allows you to deposit using your saved cards or bank accounts via the Google Pay app. Fast, secure, and convenient for Android users at Bybid9.',
    pros: ['Instant deposits', 'No fees', 'Secure tokenised payments', 'Works on all Android devices'],
    cons: ['Android only', 'Requires Google Pay app'],
    how: 'Go to Deposit. Select Google Pay. Authenticate with your fingerprint or PIN. Confirm payment.'
  },
  {
    id: 'applepay', icon: 'ri-apple-line', name: 'Apple Pay', badge: 'iOS Exclusive', badgeColor: 'text-white/60',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: '< 15 minutes', fees: 'Free', avail: '24/7',
    desc: 'Apple Pay lets you deposit securely using Face ID or Touch ID on your iPhone or iPad. No card details are shared with Bybid9.',
    pros: ['Instant deposits', 'No fees', 'Face ID / Touch ID security', 'No card details shared'],
    cons: ['iOS / macOS only'],
    how: 'Go to Deposit. Select Apple Pay. Authenticate with Face ID or Touch ID. Done.'
  },
  {
    id: 'visa', icon: 'ri-visa-line', name: 'Visa', badge: 'Widely Accepted', badgeColor: 'text-[#47CAF1]',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: '< 30 minutes', fees: 'Free', avail: '24/7',
    desc: 'Visa is one of the most widely accepted payment methods worldwide. Deposit instantly using your Visa debit or credit card at Bybid9.',
    pros: ['Instant deposits', 'Globally accepted', 'No deposit fees'],
    cons: ['Withdrawals take 3 to 5 business days'],
    how: 'Go to Deposit. Select Visa. Enter card details. Confirm payment.'
  },
  {
    id: 'mastercard', icon: 'ri-mastercard-line', name: 'Mastercard', badge: 'Global Coverage', badgeColor: 'text-red-400',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: '< 30 minutes', fees: 'Free', avail: '24/7',
    desc: 'Mastercard is accepted globally and provides instant deposits at Bybid9. Use your Mastercard debit or credit card to fund your account.',
    pros: ['Instant deposits', 'Globally accepted', 'No deposit fees'],
    cons: ['Withdrawals take 3 to 5 business days'],
    how: 'Go to Deposit. Select Mastercard. Enter card details. Confirm payment.'
  },
  {
    id: 'amopay', icon: 'ri-secure-payment-line', name: 'Amopay', badge: 'AU Specialist', badgeColor: 'text-[#47CAF1]',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: 'instant', fees: 'Free', avail: '24/7',
    desc: 'Amopay is an Australian payment specialist offering fast and secure transactions tailored for the local market at Bybid9. Amopay supports cryptocurrency deposits and withdrawals.',
    pros: ['Instant deposits', 'Fast withdrawals', 'Cryptocurrency deposits and withdrawals', 'AU-focused service', 'No fees'],
    cons: ['Available in Australia only'],
    how: 'Go to Deposit. Select Amopay. Follow the on-screen instructions. Confirm payment.'
  },
  {
    id: 'speedpay', icon: 'ri-speed-line', name: 'SpeedPay', badge: 'Fast Transfers', badgeColor: 'text-orange-400',
    minDep: 'AUD 10', minWith: 'AUD 30', depSpeed: 'Instant', withSpeed: 'instant', fees: 'Free', avail: '24/7',
    desc: 'SpeedPay is designed for fast, hassle-free transfers. Deposits and withdrawals are instant at Bybid9.',
    pros: ['Instant deposits', 'Ultra fast withdrawals', 'No fees'],
    cons: ['Limited to supported regions'],
    how: 'Go to Deposit. Select SpeedPay. Enter transfer details. Confirm.'
  },
];

export default function DepositsSection() {
  const [selected, setSelected] = useState('payid');
  const method = METHODS.find((m) => m.id === selected)!;

  return (
    <section className="relative py-14 sm:py-16 md:py-20 px-4 sm:px-5 md:px-8 overflow-hidden" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #091520 50%, #0d1117 100%)' }}>
      <div className="section-divider absolute top-0 left-0 w-full"></div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(71,202,241,0.05) 0%, transparent 60%)' }}></div>

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <div className="badge-blue mb-5"><i className="ri-bank-card-line"></i> Payment Methods</div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-3 px-1">Deposits &amp; Withdrawals at Bybid9</h2>
          <p className="text-[#56626B] text-base max-w-2xl mx-auto leading-relaxed">
            Bybid9 supports 8 payment methods tailored for Australian players. All deposits are instant, all withdrawals are fast, and all transactions are completely free of Bybid9 fees. Min deposit AUD 10. <strong className="text-[#47CAF1]">Our bank account for deposit are always changing. Always check the available bank account before depositing.</strong>
          </p>
        </div>

        {/* Detail panel */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-1 flex flex-row lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 custom-scroll snap-x snap-mandatory lg:snap-none scroll-pl-1 -mx-1 px-1 touch-pan-x">
            {METHODS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelected(m.id)}
                className="flex-shrink-0 flex items-center gap-2 px-3 py-3 sm:py-2.5 min-h-[44px] lg:min-h-0 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer whitespace-nowrap snap-start"
                style={{
                  background: selected === m.id ? 'rgba(71,202,241,0.15)' : 'rgba(71,202,241,0.04)',
                  border: selected === m.id ? '1px solid rgba(71,202,241,0.5)' : '1px solid rgba(71,202,241,0.1)',
                  color: selected === m.id ? '#47CAF1' : '#56626B',
                  boxShadow: selected === m.id ? '0 0 12px rgba(71,202,241,0.15)' : 'none',
                }}
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${m.icon} text-sm`}></i>
                </div>
                {m.name}
              </button>
            ))}
          </div>

          <div className="lg:col-span-4 glass-card rounded-2xl p-5 sm:p-6 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-5 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0" style={{ background: 'rgba(71,202,241,0.1)', border: '1px solid rgba(71,202,241,0.25)' }}>
                  <i className={`${method.icon} text-[#47CAF1] text-2xl`}></i>
                </div>
                <div>
                  <h3 className="text-white font-black text-xl">{method.name}</h3>
                  <span className={`text-xs font-bold ${method.badgeColor}`}>{method.badge}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center w-full sm:w-auto">
                <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.15)' }}>
                  <div className="text-emerald-400 font-black text-sm">{method.depSpeed}</div>
                  <div className="text-[#56626B] text-[10px]">Deposit</div>
                </div>
                <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(71,202,241,0.08)', border: '1px solid rgba(71,202,241,0.15)' }}>
                  <div className="text-[#47CAF1] font-black text-sm">{method.withSpeed}</div>
                  <div className="text-[#56626B] text-[10px]">Withdrawal</div>
                </div>
                <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="text-white font-black text-sm">{method.fees}</div>
                  <div className="text-[#56626B] text-[10px]">Fees</div>
                </div>
              </div>
            </div>

            <p className="text-[#56626B] text-sm leading-relaxed mb-6">{method.desc}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-thumb-up-line text-emerald-400"></i> Advantages
                </h4>
                <ul className="space-y-2">
                  {method.pros.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-[#56626B] text-xs">
                      <i className="ri-check-double-line text-emerald-400 text-sm flex-shrink-0 mt-0.5"></i>{p}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-yellow-400"></i> Things to Note
                </h4>
                <ul className="space-y-2">
                  {method.cons.map((c) => (
                    <li key={c} className="flex items-start gap-2 text-[#56626B] text-xs">
                      <i className="ri-alert-line text-yellow-400 text-sm flex-shrink-0 mt-0.5"></i>{c}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-xl p-4" style={{ background: 'rgba(71,202,241,0.05)', border: '1px solid rgba(71,202,241,0.15)' }}>
              <h4 className="text-[#47CAF1] font-bold text-xs mb-2 flex items-center gap-2">
                <i className="ri-guide-line"></i> How to Use {method.name} at Bybid9
              </h4>
              <p className="text-[#56626B] text-xs leading-relaxed">{method.how}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 sm:mt-10 glass-card rounded-2xl p-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 text-center sm:text-left">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: 'rgba(71,202,241,0.1)', border: '1px solid rgba(71,202,241,0.25)' }}>
            <i className="ri-shield-check-line text-[#47CAF1] text-xl"></i>
          </div>
          <div className="flex-1">
            <h4 className="text-white font-bold text-sm mb-1">All Transactions Are 100% Secure</h4>
            <p className="text-[#56626B] text-xs leading-relaxed">Every deposit and withdrawal at Bybid9 is protected by 256 bit SSL encryption and ThreatMetrix fraud prevention. Bybid9 never charges fees on deposits or withdrawals. Min deposit AUD 10. Our bank account for deposit are always changing. Always make sure to check the available bank account before depositing.</p>
          </div>
          <RegisterLink section="deposits" linkLabel="Deposit Now" href="https://bybid9.tips/RFLANDINGPAGESEO" className="btn-premium text-xs px-6 py-3 sm:py-2.5 min-h-[48px] sm:min-h-0 inline-flex items-center justify-center whitespace-nowrap flex-shrink-0 w-full sm:w-auto"><span>Deposit Now</span></RegisterLink>
        </div>
      </div>
    </section>
  );
}
